import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, retry, switchMap, tap, throwError, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { decodeJwtPayload } from '../../../helpers/jwt.helper';
import { LocalStorageClass } from '../../../helpers/local-storage-helper';
import { AuthService } from '../../../services/auth/auth.service';
import { PostLoginService } from '../../../services/auth/post-login.service';
import { ThirdPartyLoginService } from '../../../services/auth/third-party-login.service';
import { LoginResponse } from '../../../types/auth/login-data.type';

export type OAuthProvider = 'rba' | 'ht' | 'telemachhr';

@Component({
  selector: 'jenz-oauth-callback',
  imports: [],
  template: `<div class="callback-loading"></div>`,
  styleUrl: './oauth-callback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OAuthCallback implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly thirdPartyLoginService = inject(ThirdPartyLoginService);
  private readonly authService = inject(AuthService);
  private readonly postLogin = inject(PostLoginService);

  private readonly redirectUris: Record<OAuthProvider, string> = {
    rba: 'https://web.jenz.app/oauth-callback',
    ht: 'https://web.jenz.app/ht-oauth-callback',
    telemachhr: 'https://web.jenz.app/callback-telemachhr',
  };

  ngOnInit(): void {
    const token = LocalStorageClass.retrieveTokenFromStorage();
    if (token?.length) {
      this.router.navigate(['']);
      return;
    }

    const provider = this.route.snapshot.data['provider'] as OAuthProvider;
    const code = this.route.snapshot.queryParamMap.get('code');

    if (!code) {
      this.router.navigate(['login']);
      return;
    }

    const tenantName = environment.production
      ? LocalStorageClass.getThirdPartyLoginProvider() ?? provider
      : 'jenzstaging';

    LocalStorageClass.removeThirdPartyLoginProvider();

    const request$ =
      provider === 'rba'
        ? this.thirdPartyLoginService.getRbaAlbaniaToken(tenantName, code)
        : this.thirdPartyLoginService.microsoft365Login(
            tenantName,
            code,
            this.redirectUris[provider],
          );

    request$
      .pipe(
        switchMap((response: LoginResponse) => {
          if (!response?.token) {
            this.router.navigate(['login']);
            return of(null);
          }
          return this.handleApproval(response);
        }),
        catchError(() => {
          this.router.navigate(['login']);
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * After SSO returns a token, gate on the registration-approval status like
   * mobile: approved/unknown → log in; pending → waiting screen; disapproved →
   * back to login. Errors fall through to a normal login.
   */
  private handleApproval(response: LoginResponse) {
    return this.authService.approvalStatus(response.token).pipe(
      // A freshly minted SSO token can be briefly invalid server-side. Retry
      // transient 401/403/5xx/connection errors with backoff (0.5s/1s/2s)
      // before falling through to a normal login (§4.5 of the auth spec).
      retry({
        count: 3,
        delay: (error, retryCount) => {
          const status = (error as HttpErrorResponse)?.status ?? 0;
          const retryable = status === 0 || status === 401 || status === 403 || status >= 500;
          if (!retryable) return throwError(() => error);
          return timer([500, 1000, 2000][retryCount - 1] ?? 2000);
        },
      }),
      tap((result) => {
        switch (result?.status) {
          case 'pending': {
            const payload = decodeJwtPayload(response.token);
            const email =
              (payload?.['email'] ??
                payload?.['username'] ??
                payload?.['identifier'] ??
                '') as string;
            // Persist as tempToken so a reload keeps the user locked on the
            // waiting page until approved (no real token is stored).
            LocalStorageClass.setTempRegistrationToken(response.token);
            this.router.navigate(['login', 'waiting-for-approval'], {
              state: { email, token: response.token },
            });
            break;
          }
          case 'disapproved':
            this.router.navigate(['login'], { state: { disapproved: true } });
            break;
          default:
            // 'approved' / 'unknown' / anything else → normal login.
            this.postLogin.completeLoginWithToken(response.token, response.refreshToken);
        }
      }),
      catchError(() => {
        // Approval status unavailable — proceed with a normal login rather than
        // stranding a user who holds a valid token.
        this.postLogin.completeLoginWithToken(response.token, response.refreshToken);
        return of(null);
      }),
    );
  }
}
